/**
 * Emoji for the composer's ":" autocomplete (EmojiCatalog port): emojibase's Slack (iamcal) shortcodes plus GitHub
 * aliases — the same sets the API parser resolves (apps/api/src/richtext/emoji.ts). The JSON (~90 KB) is a lazy chunk,
 * fetched the first time someone types ":xx"; the PWA precache keeps it available offline after that.
 */
import type { EmojiCatalog, EmojiEntry } from "@bundle/core";

type ShortcodeMap = Record<string, string | string[]>;

/** emojibase hexcodes omit VS16 for single code points; add it so text-default symbols (❤️, ☕️) render as emoji. */
function hexToUnicode(hexcode: string): string {
  const cps = hexcode.split("-").map((h) => Number.parseInt(h, 16));
  const s = String.fromCodePoint(...cps);
  return cps.length === 1 && cps[0]! < 0x1f000 ? `${s}️` : s;
}

function entries(sources: ShortcodeMap[]): EmojiEntry[] {
  const byHex = new Map<string, string[]>();
  for (const source of sources) {
    for (const [hexcode, value] of Object.entries(source)) {
      const names = byHex.get(hexcode) ?? [];
      for (const name of Array.isArray(value) ? value : [value]) if (!names.includes(name)) names.push(name);
      byHex.set(hexcode, names);
    }
  }
  return [...byHex].map(([hexcode, names]) => ({ unicode: hexToUnicode(hexcode), names }));
}

let loading: Promise<EmojiEntry[]> | undefined;

export const webEmojiCatalog: EmojiCatalog = {
  load() {
    loading ??= Promise.all([import("emojibase-data/en/shortcodes/iamcal.json"), import("emojibase-data/en/shortcodes/github.json")]).then(
      ([iamcal, github]) => entries([iamcal.default as ShortcodeMap, github.default as ShortcodeMap]),
      (error: unknown) => {
        loading = undefined;
        throw error;
      },
    );
    return loading;
  },
};
